(() => {
  const money = (cents, format) => {
    const value = Number(cents || 0) / 100;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(Number(cents || 0), format || window.Shopify.money_format || '£{{amount}}');
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: 'GBP' }).format(value);
  };

  const planFrequency = (name, fallback = '1 month') => {
    const value = String(name || '').trim();
    if (!value) return fallback;
    const cleaned = value
      .replace(/^(?:delivery|deliver(?:ed)?|ships?|shipped)\s+every\s+/i, '')
      .replace(/^every\s+/i, '')
      .trim();
    return cleaned || fallback;
  };

  class JCRetaProduct extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.moneyFormat = this.dataset.moneyFormat || '£{{amount}}';
      this.subscriptionQty = Math.max(1, Number(this.dataset.subscriptionQuantity || 1));
      this.oneTimeQty = Math.max(1, Number(this.dataset.oneTimeQuantity || 1));
      this.variants = JSON.parse(this.querySelector('[data-jc-variant-json]')?.textContent || '[]');
      this.variantSelect = this.querySelector('[data-jc-variant-select]');
      this.variantInput = this.querySelector('[data-jc-variant-input]');
      this.planInput = this.querySelector('[data-jc-selling-plan-input]');
      this.planSelect = this.querySelector('[data-jc-plan-select]');
      this.quantityInput = this.querySelector('[data-jc-quantity-input]');
      this.subscriptionButton = this.querySelector('[data-jc-purchase="subscription"]');
      this.oneTimeButton = this.querySelector('[data-jc-purchase="one_time"]');
      this.submitButton = this.querySelector('[data-jc-submit]');
      this.expressButton = this.querySelector('[data-jc-express]');
      this.error = this.querySelector('[data-jc-error]');
      this.subscriptionDetailsLink = this.querySelector('[data-jc-subscription-details]');
      this.subscriptionDetailsDot = this.querySelector('[data-jc-subscription-details-dot]');
      this.sealDetailsSource = this.querySelector('[data-jc-seal-source]');
      this.sealDetailsTrigger = null;
      const hasVariantCards = this.querySelectorAll('[data-jc-variant-card]').length > 0;
      this.selectedPurchase = hasVariantCards ? 'one_time' : (this.dataset.defaultPurchase === 'one_time' ? 'one_time' : 'subscription');
      if (hasVariantCards) {
        const qtyRow = this.querySelector('.jc-product-quantity-row');
        if (qtyRow) qtyRow.style.display = 'none';
      }

      this.querySelectorAll('[data-jc-gallery-thumb]').forEach((button) => {
        button.addEventListener('click', () => this.showMedia(button.dataset.mediaId));
      });
      this.variantSelect?.addEventListener('change', () => this.updateVariant(this.variantSelect.value));
      Array.from(this.querySelectorAll('[data-jc-variant-card]')).forEach((card, index) => {
        card.addEventListener('click', () => {
          const variant = this.variants[index];
          if (!variant) return;
          this.variant = variant;
          if (this.variantInput) this.variantInput.value = variant.id;
          if (variant.featured_media_id) this.showMedia(variant.featured_media_id);
          if (this.submitButton) this.submitButton.disabled = !variant.available;
          if (this.expressButton) this.expressButton.disabled = !variant.available;
          this.querySelectorAll('[data-jc-stock-label]').forEach((el) => {
            el.textContent = variant.available ? el.dataset.availableText : el.dataset.soldText;
          });
          this.setPurchase('one_time');
        });
      });
      this.subscriptionButton?.addEventListener('click', (event) => {
        if (event.target.closest('[data-jc-plan-select]')) return;
        this.setPurchase('subscription');
      });
      this.subscriptionButton?.addEventListener('keydown', (event) => {
        if (event.target.closest('[data-jc-plan-select]')) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.setPurchase('subscription');
        }
      });
      this.planSelect?.addEventListener('click', (event) => event.stopPropagation());
      this.planSelect?.addEventListener('change', () => {
        this.selectedPlanId = this.planSelect.value;
        this.setPurchase('subscription');
      });
      this.oneTimeButton?.addEventListener('click', () => this.setPurchase('one_time'));
      this.subscriptionDetailsLink?.addEventListener('click', (event) => this.openSubscriptionDetails(event));
      this.expressButton?.addEventListener('click', () => this.expressCheckout());
      this.initSealSubscriptionDetails();
      this.initRetaModals();
      const allCards = Array.from(this.querySelectorAll('[data-jc-variant-card]'));
      const transformationIndex = allCards.findIndex((card) => card.querySelector('.jc-product-variant-card__name')?.textContent?.includes('TRANSFORMATION'));
      const initialVariant = transformationIndex >= 0 ? this.variants[transformationIndex] : null;
      this.updateVariant(initialVariant ? initialVariant.id : (this.variantSelect?.value || this.variantInput?.value));
    }

    initRetaModals() {
      this.querySelectorAll('[data-jc-modal-open]').forEach((button) => {
        button.addEventListener('click', () => this.openRetaModal(button.dataset.jcModalOpen));
      });
      this.querySelectorAll('[data-jc-modal-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeRetaModal(button.closest('[data-jc-modal]')));
      });
      this.querySelectorAll('[data-jc-modal]').forEach((modal) => {
        modal.addEventListener('click', (event) => {
          if (event.target === modal) this.closeRetaModal(modal);
        });
      });
      this.retaEscapeHandler = (event) => {
        if (event.key === 'Escape') this.closeRetaModal(this.querySelector('[data-jc-modal]:not([hidden])'));
      };
      document.addEventListener('keydown', this.retaEscapeHandler);
    }

    openRetaModal(id) {
      const modal = this.querySelector(`[data-jc-modal="${id}"]`);
      if (!modal) return;
      modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      modal.querySelector('[data-jc-modal-close]')?.focus();
    }

    closeRetaModal(modal) {
      if (!modal) return;
      modal.hidden = true;
      document.documentElement.style.overflow = '';
    }

    initSealSubscriptionDetails() {
      if (!this.subscriptionDetailsLink || !this.sealDetailsSource) return;

      const refresh = () => {
        window.requestAnimationFrame(() => this.bindSealSubscriptionDetails());
      };

      this.sealWidgetListener = refresh;
      document.addEventListener('sealsubs:subscription_widget_created', this.sealWidgetListener);

      this.sealDetailsObserver = new MutationObserver(refresh);
      this.sealDetailsObserver.observe(this.sealDetailsSource, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['href', 'aria-label', 'title']
      });

      refresh();
      window.setTimeout(refresh, 500);
      window.setTimeout(refresh, 1500);
      window.setTimeout(refresh, 3500);
    }

    disconnectedCallback() {
      if (this.sealWidgetListener) {
        document.removeEventListener('sealsubs:subscription_widget_created', this.sealWidgetListener);
      }
      this.sealDetailsObserver?.disconnect();
    }

    sealDetailsCandidate() {
      if (!this.sealDetailsSource) return null;
      const elements = Array.from(this.sealDetailsSource.querySelectorAll('a, button, [role="button"]'));
      const readableText = (element) => [
        element.textContent,
        element.getAttribute('aria-label'),
        element.getAttribute('title')
      ].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();

      return elements.find((element) => /subscription\s*(details?|information)|details?\s*(of|about)?\s*subscription|more\s*(info|information)/i.test(readableText(element)))
        || elements.find((element) => {
          const signature = `${element.className || ''} ${element.id || ''}`;
          return /subscription.*(detail|info)|(detail|info).*subscription|sls.*(detail|info)/i.test(signature);
        })
        || elements.find((element) => {
          const href = element.getAttribute('href') || '';
          return /seal|subscription/i.test(href) && /detail|info|terms/i.test(href);
        })
        || null;
    }

    bindSealSubscriptionDetails() {
      const candidate = this.sealDetailsCandidate();
      if (!candidate || !this.subscriptionDetailsLink) return false;

      const appText = [
        candidate.textContent,
        candidate.getAttribute('aria-label'),
        candidate.getAttribute('title')
      ].find((value) => String(value || '').trim());
      if (appText) this.subscriptionDetailsLink.textContent = String(appText).replace(/\s+/g, ' ').trim();

      const href = candidate.getAttribute('href');
      if (href && href !== '#' && !/^javascript:/i.test(href)) {
        this.subscriptionDetailsLink.href = candidate.href || href;
        const target = candidate.getAttribute('target');
        const rel = candidate.getAttribute('rel');
        if (target) this.subscriptionDetailsLink.setAttribute('target', target);
        else this.subscriptionDetailsLink.removeAttribute('target');
        if (rel) this.subscriptionDetailsLink.setAttribute('rel', rel);
        else this.subscriptionDetailsLink.removeAttribute('rel');
        this.subscriptionDetailsLink.dataset.sealDetailsMode = 'link';
        this.sealDetailsTrigger = null;
      } else {
        this.subscriptionDetailsLink.href = '#';
        this.subscriptionDetailsLink.dataset.sealDetailsMode = 'trigger';
        this.sealDetailsTrigger = candidate;
      }

      this.subscriptionDetailsLink.hidden = false;
      this.subscriptionDetailsLink.removeAttribute('aria-disabled');
      if (this.subscriptionDetailsDot) this.subscriptionDetailsDot.hidden = false;
      return true;
    }

    openSubscriptionDetails(event) {
      if (!this.subscriptionDetailsLink || this.subscriptionDetailsLink.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }
      if (this.subscriptionDetailsLink.dataset.sealDetailsMode !== 'trigger') return;
      event.preventDefault();
      this.sealDetailsTrigger?.dispatchEvent(new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      }));
    }

    currentVariant(id) {
      return this.variants.find((variant) => String(variant.id) === String(id)) || this.variants[0];
    }

    showMedia(id) {
      if (!id) return;
      this.querySelectorAll('[data-jc-gallery-item]').forEach((item) => item.classList.toggle('is-active', String(item.dataset.mediaId) === String(id)));
      this.querySelectorAll('[data-jc-gallery-thumb]').forEach((item) => item.classList.toggle('is-active', String(item.dataset.mediaId) === String(id)));
    }

    updatePlanOptions(variant) {
      if (!this.planSelect) return;
      const plans = Array.isArray(variant?.selling_plans) ? variant.selling_plans : [];
      const previous = String(this.selectedPlanId || this.planSelect.value || '');
      this.planSelect.innerHTML = '';
      plans.forEach((plan) => {
        const option = document.createElement('option');
        option.value = String(plan.id);
        option.textContent = planFrequency(plan.name, '1 month');
        this.planSelect.appendChild(option);
      });
      const selected = plans.find((plan) => String(plan.id) === previous) || plans[0] || null;
      this.selectedPlanId = selected ? String(selected.id) : '';
      this.planSelect.value = this.selectedPlanId;
    }

    selectedPlan() {
      const plans = Array.isArray(this.variant?.selling_plans) ? this.variant.selling_plans : [];
      return plans.find((plan) => String(plan.id) === String(this.selectedPlanId || this.planSelect?.value)) || plans[0] || null;
    }

    updateVariant(id) {
      const variant = this.currentVariant(id);
      if (!variant) return;
      this.variant = variant;
      if (this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (variant.featured_media_id) this.showMedia(variant.featured_media_id);
      const hasPlan = Array.isArray(variant.selling_plans) && variant.selling_plans.length > 0;
      this.updatePlanOptions(variant);
      if (this.subscriptionButton) {
        this.subscriptionButton.toggleAttribute('data-disabled', !hasPlan);
        this.subscriptionButton.setAttribute('aria-disabled', String(!hasPlan));
        this.subscriptionButton.tabIndex = hasPlan ? 0 : -1;
      }
      if (!hasPlan && this.selectedPurchase === 'subscription') this.selectedPurchase = 'one_time';
      this.setPurchase(this.selectedPurchase, true);
      if (this.submitButton) this.submitButton.disabled = !variant.available;
      if (this.expressButton) this.expressButton.disabled = !variant.available;
      this.querySelectorAll('[data-jc-stock-label]').forEach((el) => { el.textContent = variant.available ? el.dataset.availableText : el.dataset.soldText; });
    }

    setPurchase(type, silent = false) {
      if (type === 'subscription' && this.subscriptionButton?.getAttribute('aria-disabled') === 'true') type = 'one_time';
      this.selectedPurchase = type;
      const isSub = type === 'subscription';
      this.subscriptionButton?.classList.toggle('is-selected', isSub);
      this.subscriptionButton?.setAttribute('aria-pressed', String(isSub));
      this.oneTimeButton?.classList.toggle('is-selected', !isSub);
      this.oneTimeButton?.setAttribute('aria-pressed', String(!isSub));
      const variantCards = Array.from(this.querySelectorAll('[data-jc-variant-card]'));
      const selectedIndex = this.variants.findIndex((v) => String(v.id) === String(this.variant?.id));
      variantCards.forEach((card, index) => {
        card.classList.toggle('is-selected', !isSub && index === selectedIndex);
        card.setAttribute('aria-pressed', String(!isSub && index === selectedIndex));
      });

      const availablePlan = this.selectedPlan();
      const plan = isSub ? availablePlan : null;
      if (this.planSelect) this.planSelect.disabled = !isSub || !availablePlan;
      if (this.planInput) {
        this.planInput.value = plan?.id || '';
        this.planInput.disabled = !plan;
      }
      if (this.quantityInput) this.quantityInput.value = isSub ? this.subscriptionQty : this.oneTimeQty;

      const unitPrice = isSub && plan ? plan.price : this.variant?.price || 0;
      const unitCompare = isSub ? (this.variant?.price || plan?.compare_at_price || unitPrice) : (this.variant?.compare_at_price || 0);
      const quantity = isSub ? this.subscriptionQty : this.oneTimeQty;
      const total = unitPrice * quantity;
      const totalCompare = unitCompare > unitPrice ? unitCompare * quantity : 0;
      const savePct = totalCompare > total ? Math.round((totalCompare - total) * 100 / totalCompare) : 0;
      const subscriptionSavePct = availablePlan && (this.variant?.price || 0) > (availablePlan.price || 0)
        ? Math.round(((this.variant.price - availablePlan.price) * 100) / this.variant.price)
        : 0;

      this.querySelectorAll('[data-jc-sub-discount]').forEach((el) => {
        el.textContent = subscriptionSavePct ? `${subscriptionSavePct}% OFF` : '';
        el.hidden = !subscriptionSavePct;
      });
      this.querySelectorAll('[data-jc-sub-unit-price]').forEach((el) => { el.textContent = money(availablePlan?.price || this.variant?.price || 0, this.moneyFormat); });
      this.querySelectorAll('[data-jc-sub-compare]').forEach((el) => { el.textContent = money((this.variant?.price || 0) * this.subscriptionQty, this.moneyFormat); });
      this.querySelectorAll('[data-jc-one-unit-price]').forEach((el) => { el.textContent = money(this.variant?.price || 0, this.moneyFormat); });
      this.querySelectorAll('[data-jc-summary-current]').forEach((el) => { el.textContent = money(total, this.moneyFormat); });
      this.querySelectorAll('[data-jc-summary-compare]').forEach((el) => {
        el.textContent = totalCompare ? money(totalCompare, this.moneyFormat) : '';
        el.hidden = !totalCompare;
      });
      this.querySelectorAll('[data-jc-summary-save]').forEach((el) => {
        el.textContent = savePct ? `SAVE ${savePct}%` : '';
        el.hidden = !savePct;
      });
      this.dispatchEvent(new CustomEvent('jc:purchase-change', { bubbles: true, detail: { type, variant: this.variant, plan } }));
      if (!silent && this.error) this.error.textContent = '';
    }

    async expressCheckout() {
      if (!this.variant?.available) return;
      const form = this.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      this.error.textContent = '';
      this.expressButton.disabled = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add this item.');
        window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
      } catch (error) {
        this.error.textContent = error.message || 'Could not start checkout. Please try Add to cart.';
        this.expressButton.disabled = false;
      }
    }
  }

  if (!customElements.get('jc-reta-product')) customElements.define('jc-reta-product', JCRetaProduct);
})();
